import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import debounce from 'lodash.debounce';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { getPercentageSafe } from '../../../Utils/MathUtils';
import { chartSkeletonScreen, mobileChartSkeletonScreen } from '../../../SkeletonScreens/SkeletonScreen';
import { denormalizeSessions } from '../../../Reducks/Sessions/SessionsUtils';
import Menu from '../../../Components/Menu/Menu';
import { getChartTypeMenuItems } from '../../../Components/Menu/MenuItemsHelper';
import { CSV, HIGHCHARTS_MENU, PNG, XLS } from '../../../Utils/Constants';
import applyExporting from 'highcharts/modules/exporting';
import applyOffline from 'highcharts/modules/offline-exporting';
import applyExportData from 'highcharts/modules/export-data';
import './SessionsChartContainer.css';
import { trackAnalyticsChartType, trackAnalyticsFeature } from '../../../Telemetry/Mixpanel/Features/analyticsTracker';
import {
  DOWNLOAD_CHART,
  MixpanelChartType,
  PNG as mixpanel_PNG,
  CSV as mixpanel_CSV,
  EXCEL as mixpanel_EXCEL,
  SESSIONS_OVERVIEW,
  VALUE_ADD
} from '../../../Telemetry/Mixpanel/Constants/mixpanelConstants';
import DownloadButton from '../../../Components/Buttons/DownloadIcon';
import { getSafeTimezone } from '../../../Utils/MomentUtils';

export class SessionsChartContainer extends Component {
  static contextTypes = {
    t: PropTypes.func.isRequired
  };
  static propTypes = {
    sessions: PropTypes.object,
    focusSessionInTable: PropTypes.func,
    focusedSessionKeyInChart: PropTypes.string,
    isBig: PropTypes.bool,
    user: PropTypes.object
  };
  constructor(props) {
    super(props);
    this.chartRef = createRef();
    this.filteredSelectedSessions = [];
  }

  componentDidMount() {
    applyExporting(Highcharts);
    applyOffline(Highcharts);
    applyExportData(Highcharts);
  }

  componentDidUpdate() {
    this.hoverOnSession(this.props.focusedSessionKeyInChart);
  }

  onClickHandler = id => {
    trackAnalyticsFeature(VALUE_ADD, SESSIONS_OVERVIEW, DOWNLOAD_CHART);
    if (id === PNG) {
      this.chartRef.current.chart.exportChartLocal();
      trackAnalyticsChartType(MixpanelChartType.SESSIONS_OVERVIEW_CHART, mixpanel_PNG);
    } else if (id === XLS) {
      this.chartRef.current.chart.downloadXLS();
      trackAnalyticsChartType(MixpanelChartType.SESSIONS_OVERVIEW_CHART, mixpanel_EXCEL);
    } else if (id === CSV) {
      this.chartRef.current.chart.downloadCSV();
      trackAnalyticsChartType(MixpanelChartType.SESSIONS_OVERVIEW_CHART, mixpanel_CSV);
    }
  };

  render() {
    const { fetching, sessions } = this.props.sessions;
    const { isBig, user } = this.props;
    const focusSessionInTable = this.props.focusSessionInTable;
    const { filteredSelectedSessionKeys, byKey } = sessions;
    const { t } = this.context;
    const detailsMenu = [];
    const menuItems = getChartTypeMenuItems(HIGHCHARTS_MENU);
    this.filteredSelectedSessions = denormalizeSessions(filteredSelectedSessionKeys, byKey).reverse();

    menuItems.map(item =>
      detailsMenu.push({
        onClick: () => this.onClickHandler(item.id),
        text: item.title,
        iconClassName: item.iconClass,
        id: item.id
      })
    );

    return (
      <div className={`sessionsChartLayout ${fetching && 'fetching'}`}>
        {fetching ? (
          isBig ? (
            chartSkeletonScreen()
          ) : (
            mobileChartSkeletonScreen()
          )
        ) : (
          <div>
            <div className="panelHeader">
              <div className="panelTitle">{t('header.chartHeader.attendance')}</div>
              <div className="sessionsChartHeaderMenu">
                <Menu
                  isBig={isBig}
                  menuKey="card_header_menu"
                  buttonName={t('buttons.downloadCamelCase')}
                  detailsMenu={detailsMenu}
                  tooltipText={t('chart.downloadTooltipText')}
                  buttonComponent={<DownloadButton isBig={isBig} />}
                />
              </div>
            </div>
            <div className="panelBody">
              <HighchartsReact
                ref={this.chartRef}
                highcharts={Highcharts}
                constructorType={'chart'}
                options={this.getChartOptions(
                  this.filteredSelectedSessions,
                  focusSessionInTable,
                  this.props.isBig,
                  user
                )}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  getChartOptions = (sessions, focusSessionInTable, isBig, user) => {
    const { t } = this.context;
    const darkMode = user && user.darkMode;
    return {
      colors: ['#00c2ff', '#8a5be4'],
      chart: {
        height: isBig ? 350 : 220,
        zoomType: 'x',
        backgroundColor: darkMode ? '#444444' : '#ffffff'
      },
      legend: {
        enabled: true,
        align: isBig ? 'left' : 'center',
        verticalAlign: isBig ? 'top' : 'bottom',
        y: isBig ? -24 : 0,
        x: isBig ? -10 : 0,
        itemMarginBottom: isBig ? 24 : 0,
        itemStyle: {
          color: darkMode ? '#ffffff' : '#444444'
        }
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false,
        csv: {
          decimalPoint: null
        },
        type: 'image/png',
        url: 'https://export.highcharts.com/'
      },
      xAxis: {
        categories: this.getXAxisCountArray(sessions),
        crosshair: {
          width: '1',
          color: '#b4b6c3',
          dashStyle: 'solid'
        },
        labels: {
          style: {
            color: darkMode ? '#ffffff' : '#666666'
          }
        }
      },
      yAxis: [
        {
          min: 0,
          title: '',
          tickAmount: 5,
          allowDecimals: false,
          labels: {
            style: {
              color: darkMode ? '#ffffff' : '#666666'
            }
          }
        }
      ],
      tooltip: isBig
        ? {
            useHTML: true,
            shared: true,
            formatter: SessionsChartContainer.formatTooltip,
            borderRadius: '2',
            borderWidth: '0',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.12), 0 5px 15px 0 rgba(0, 0, 0, 0.08)',
            backgroundColor: darkMode ? '#2A2A2A' : '#ffffff',
            style: {
              color: darkMode ? '#ffffff' : '#444444',
              fontSize: '0.75em',
              fontFamily: 'Lato, sans-serif'
            }
          }
        : { enabled: false },
      plotOptions: {
        series: {
          turboThreshold: 2000,
          maxPointWidth: 50,
          point: {
            events: {
              mouseOver: debounce(function() {
                if (this.options && this.options.sessionKey) {
                  focusSessionInTable(this.options.sessionKey);
                }
              }, 150),
              mouseOut: debounce(function() {
                focusSessionInTable(null);
              }, 150)
            }
          },
          animation: {
            duration: 500,
            enabled: true
          }
        },
        line: {
          marker: {
            enabled: false
          }
        }
      },
      series: [
        {
          name: t('tableTopStat.registrants'),
          type: 'line',
          data: this.getRegistrantCountArray(sessions)
        },
        {
          name: t('tableTopStat.attendees'),
          type: 'line',
          data: this.getAttendeeCountArray(sessions)
        }
      ]
    };
  };

  getRegistrantCountArray = sessions => {
    const { t } = this.context;
    if (!sessions) {
      return [];
    }
    const regCountArray = [];
    sessions.forEach(data => {
      regCountArray.push({
        y: data.registrantCount,
        sessionName: data.webinarName,
        sessionKey: data.sessionKey,
        formattedStartTime: this.getFormattedStartTime(data.startTime, data.timeZone),
        attendanceRateName: t('tableHeaderColumns.attendanceRate'),
        attendanceRate: getPercentageSafe(data.registrantsAttended, data.registrantCount) + '%'
      });
    });
    return regCountArray;
  };

  getAttendeeCountArray = sessions => {
    const { t } = this.context;
    if (!sessions) {
      return [];
    }
    const regCountArray = [];
    sessions.forEach(data => {
      regCountArray.push({
        y: data.registrantsAttended,
        sessionName: data.webinarName,
        sessionKey: data.sessionKey,
        formattedStartTime: this.getFormattedStartTime(data.startTime, data.timeZone),
        attendanceRateName: t('tableHeaderColumns.attendanceRate'),
        attendanceRate: getPercentageSafe(data.registrantsAttended, data.registrantCount) + '%'
      });
    });
    return regCountArray;
  };

  getXAxisCountArray = sessions => {
    if (!sessions) {
      return [];
    }
    const { t } = this.context;
    return sessions.map(data => {
      let { startTime, timeZone } = data;
      return moment(startTime)
        .tz(timeZone)
        .format(t('formatting.monthDay'));
    });
  };

  getFormattedStartTime = (startTime, timeZone) => {
    let tz = getSafeTimezone(timeZone);
    const { t } = this.context;
    return moment(startTime)
      .tz(tz)
      .format(t('formatting.dayDateTimeWithZone'));
  };

  static formatTooltip(tooltip, x = this.x, points = this.points) {
    let { formattedStartTime } = points[0].point;
    let data = '';
    points.forEach(point => {
      data += `<div style="display: flex; flex-direction: row; justify-content: space-between; padding-bottom: 16px">
      <div style="font-size: 16px; font-weight: 500; font-style: normal; font-stretch: normal; line-height: normal;
      letter-spacing: normal;">${point.series.name}</div>
      <div style="font-size: 18px; font-weight: bold; font-style: normal; font-stretch: normal; line-height: normal;
      letter-spacing: normal;">${point.point.y}</div>
    </div>`;
    });
    data += `<div style="display: flex; flex-direction: row; justify-content: space-between;">
      <div style="font-size: 16px; font-weight: 500; font-style: normal; font-stretch: normal; line-height: normal;
      letter-spacing: normal;">${points[0].point.attendanceRateName}</div>
      <div style="font-size: 18px; font-weight: bold; font-style: normal; font-stretch: normal; line-height: normal;
      letter-spacing: normal;">${points[0].point.attendanceRate}</div>
    </div>`;
    return `<br/>
    <div style="font-size: 16px; font-weight: bold; font-style: normal; font-stretch: normal; line-height: 1;
    letter-spacing: normal; padding-bottom: 6px;">
      ${points[0].point.sessionName}
    </div>
    <div style="font-size: 14px; font-weight: 500; font-style: normal; font-stretch: normal; line-height: normal;
    letter-spacing: normal;">
      ${formattedStartTime}
    </div>
    <hr style="border-top: 1px solid #d0d2dd; margin-top: 11px; margin-bottom: 16px;" />
    ${data}
    `;
  }

  hoverOnSession = focusedSessionKeyInChart => {
    try {
      let series = this.chartRef.current.chart.series;
      if (focusedSessionKeyInChart) {
        let i = this.filteredSelectedSessions.findIndex(x => x.sessionKey === focusedSessionKeyInChart);
        if (i === null) {
          return;
        }
        let tooltipArray = [series[0], series[1], series[2]]
          .filter(a => a)
          .map(b => b.data[i])
          .filter(k => k);
        this.chartRef.current.chart.tooltip.refresh(tooltipArray);
      } else {
        this.chartRef.current.chart.tooltip.hide();
      }
    } catch (e) {}
  };
}

export default connect(state => ({ user: state.reduced_user }))(SessionsChartContainer);



// WEBPACK FOOTER //
// ./src/GoToWebinar/Sessions/Chart/SessionsChartContainer.js